import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/Eyebrow";
import { RentalRequirements } from "@/components/site/RentalRequirements";
import { Phone, Mail, Clock, Globe } from "lucide-react";
import { breadcrumbScript } from "@/lib/schema";

export const Route = createFileRoute("/reserve")({
  component: Reserve,
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex, follow" },
      { title: "Reserve | Avolus Luxury Car Rental Vancouver" },
      { name: "description", content: "Reserve a luxury or exotic car from Avolus. Call 778-379-8881 or email info@avoluscars.com. The concierge confirms within the hour." },
      { property: "og:title", content: "Reserve | Avolus Luxury Car Rental" },
      { property: "og:description", content: "Call or email the Avolus concierge to begin your reservation." },
      { property: "og:url", content: "https://avoluscars.com/reserve" },
    ],
    links: [{ rel: "canonical", href: "https://avoluscars.com/reserve" }],
    scripts: [
      breadcrumbScript([
        { name: "Home", url: "https://avoluscars.com/" },
        { name: "Reserve", url: "https://avoluscars.com/reserve" },
      ]),
    ],
  }),
});

function Reserve() {
  return (
    <Layout>
      <section className="pt-40 pb-12">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <Reveal>
            <Eyebrow>Book Now</Eyebrow>
            <h1 className="font-display text-5xl md:text-7xl text-foreground">
              Reserve Your Luxury Car
            </h1>
            <p className="mt-6 text-muted-foreground max-w-xl">
              Ready to drive? Book online instantly, or reach our concierge directly;
              we typically confirm availability within the hour.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-16">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <Reveal>
            <div className="border border-border bg-card p-8 md:p-12">
              <div className="grid gap-10 md:grid-cols-3">
                <div>
                  <div className="flex items-center gap-3 text-foreground mb-3">
                    <Phone size={16} />
                    <span className="text-xs uppercase tracking-[0.25em]">Call</span>
                  </div>
                  <a
                    href="tel:+17783798881"
                    className="font-display text-3xl md:text-4xl text-foreground hover:underline underline-offset-4"
                  >
                    778-379-8881
                  </a>
                  <p className="mt-3 text-sm text-muted-foreground">
                    Fastest way to reserve. Speak directly with the concierge.
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-3 text-foreground mb-3">
                    <Mail size={16} />
                    <span className="text-xs uppercase tracking-[0.25em]">Email</span>
                  </div>
                  <a
                    href="mailto:info@avoluscars.com"
                    className="font-display text-3xl md:text-4xl text-foreground hover:underline underline-offset-4 break-all"
                  >
                    info@avoluscars.com
                  </a>
                  <p className="mt-3 text-sm text-muted-foreground">
                    Include dates, vehicle, and delivery location for a quick quote.
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-3 text-foreground mb-3">
                    <Globe size={16} />
                    <span className="text-xs uppercase tracking-[0.25em]">Book Online</span>
                  </div>
                  <a
                    href="/book"
                    className="font-display text-3xl md:text-4xl text-foreground hover:underline underline-offset-4"
                  >
                    Instant Booking
                  </a>
                  <p className="mt-3 text-sm text-muted-foreground">
                    Secure your vehicle online in under two minutes.
                  </p>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-border">
                <div className="flex items-center gap-3 text-foreground mb-3">
                  <Clock size={16} />
                  <span className="text-xs uppercase tracking-[0.25em]">Hours</span>
                </div>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li>Monday - Friday: <span className="text-foreground">8:00 AM - 8:00 PM</span></li>
                  <li>Saturday - Sunday: <span className="text-foreground">8:00 AM - 8:00 PM</span></li>
                </ul>
              </div>

              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href="/book"
                  className="text-xs uppercase tracking-[0.25em] px-8 py-4 bg-foreground text-background hover:bg-foreground/90 transition-colors"
                >
                  Book Online
                </a>
                <a
                  href="tel:+17783798881"
                  className="md:hidden text-xs uppercase tracking-[0.25em] px-8 py-4 border border-border text-foreground hover:bg-foreground hover:text-background transition-colors"
                >
                  Call Concierge
                </a>
                <a
                  href="/contact"
                  className="text-xs uppercase tracking-[0.25em] px-8 py-4 border border-border text-foreground hover:bg-foreground hover:text-background transition-colors"
                >
                  Send a Message
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <RentalRequirements />
    </Layout>
  );
}

